import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("light", "veil");

type Locale = "ko" | "en";

const LOCALE_STORAGE_KEY = "veil-terms-locale";
const locales: Locale[] = ["ko", "en"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
  }
> = {
  ko: {
    crumb: "Veil Terms",
    title: "Veil,",
    accentTitle: "이용약관",
    subtitle:
      "최종 업데이트일: 2026년 10월 29일. 본 서비스의 운영 주체는 Daydream Factory입니다. Veil 앱과 관련 웹페이지를 이용하는 경우 아래 약관이 적용됩니다.",
    sections: [
      {
        title: "1. 서비스 소개",
        body:
          "Veil은 별도의 회원가입 없이 사용할 수 있는 사진 보정 앱입니다. 사진에 빛과 색, 질감, 프레임을 더해 저장하고 공유할 수 있으며, 모든 처리는 사용자 기기 안에서 이루어집니다. 사진, 보정값, 프리셋, 히스토리, 필름 롤은 기기에만 저장됩니다.",
      },
      {
        title: "2. 무료 기능과 Veil Pro",
        list: [
          "촬영, 사진 불러오기, 기본 편집, 기본 저장은 무료이며 결과물에 워터마크를 넣지 않습니다.",
          "Veil Pro는 월간·연간 자동 갱신 구독 또는 평생 이용권(일회성 구매)으로 제공되며, 고화질 저장, 추가 장면 팩, 프리셋·히스토리·필름 롤의 보관 한도 해제 등을 포함합니다. 포함 범위와 가격은 결제 화면 및 스토어에 표시된 내용을 따릅니다.",
          "구독은 현재 이용 기간이 끝나기 24시간 전까지 취소하지 않으면 자동 갱신됩니다. 구독 관리와 취소는 기기의 스토어 계정 설정에서 할 수 있습니다.",
          "구매 내역은 같은 스토어 계정에서 '구매 복원' 기능으로 복원할 수 있습니다.",
          "결제·환불은 Apple App Store 및 Google Play 정책에 따릅니다.",
          "구독이 끝나면 Pro 효과를 유지한 새 저장이 제한될 수 있으나, 이미 저장한 작업은 계속 열람하고 기본 룩으로 다시 편집할 수 있습니다.",
        ],
      },
      {
        title: "3. 사진과 콘텐츠",
        list: [
          "이용자가 편집하는 사진에 대한 권리와 책임은 이용자에게 있습니다. 타인의 초상권·저작권을 침해하는 사진을 편집·공유해서는 안 됩니다.",
          "앱에 포함된 샘플 사진과 장면 설명은 기능 체험을 위한 참고 자료입니다.",
          "필터·질감·프레임의 결과는 기기, 사진, 저장 품질 설정에 따라 다르게 보일 수 있습니다.",
        ],
      },
      {
        title: "4. 데이터와 사용자 책임",
        list: [
          "사진·보정값·프리셋·히스토리·필름 롤은 기기 로컬에 저장되며, Daydream Factory는 해당 내용에 접근하지 않습니다.",
          "기기 교체·앱 삭제 시 앱 내부 데이터가 사라질 수 있으므로, 중요한 결과물은 사진 보관함에 저장하거나 별도로 관리할 책임이 이용자에게 있습니다.",
        ],
      },
      {
        title: "5. 면책",
        list: [
          "Veil은 '있는 그대로' 제공되며, 결제 등 외부 서비스 상태에 따라 일부 기능이 지연되거나 제한될 수 있습니다.",
          "본 서비스 이용 또는 데이터 유실로 발생한 손해에 대해 관련 법령이 허용하는 범위 내에서 책임이 제한될 수 있습니다.",
        ],
      },
      {
        title: "6. 지적재산권",
        body:
          "Veil 앱과 디자인, 로고, 필터·장면 콘텐츠에 대한 권리는 Daydream Factory에 있으며, 무단 복제·배포·역설계를 금지합니다. 이용자가 편집한 사진의 권리는 이용자에게 있습니다.",
      },
      {
        title: "7. 약관 변경",
        body:
          "본 약관은 서비스 개선 또는 법령 변경에 따라 수정될 수 있으며, 중요한 변경은 앱 또는 본 페이지를 통해 안내합니다.",
      },
      {
        title: "8. 문의",
        body: "본 약관에 관한 문의: support@daydreamfactory.net",
      },
    ],
  },
  en: {
    crumb: "Veil Terms",
    title: "Veil,",
    accentTitle: "Terms of Service",
    subtitle:
      "Last updated: October 29, 2026. This service is operated by Daydream Factory. The following terms apply when you use the Veil app and related web pages.",
    sections: [
      {
        title: "1. About the Service",
        body:
          "Veil is a photo editing app you can use without creating an account. You can add light, color, texture, and frames to your photos, then save and share them. All processing happens on your device, and photos, adjustments, presets, history, and film rolls are stored only on your device.",
      },
      {
        title: "2. Free Features and Veil Pro",
        list: [
          "Capturing, importing, basic editing, and basic saving are free, and no watermark is added to your results.",
          "Veil Pro is offered as a monthly or annual auto-renewing subscription or a lifetime one-time purchase. It includes high-resolution saving, additional scene packs, and lifted limits on presets, history, and film rolls. The exact scope and price follow what is shown on the purchase screen and in the store.",
          "Subscriptions renew automatically unless cancelled at least 24 hours before the end of the current period. You can manage or cancel them in your store account settings.",
          "Purchases can be restored on the same store account using the 'Restore purchase' feature.",
          "Billing and refunds follow Apple App Store and Google Play policies.",
          "When a subscription ends, new saves that keep Pro effects may be limited, but work you have already saved remains viewable and can be re-edited with the standard looks.",
        ],
      },
      {
        title: "3. Photos and Content",
        list: [
          "You own, and are responsible for, the photos you edit. Do not edit or share photos that infringe on others' likeness or copyright.",
          "Sample photos and scene descriptions in the app are reference material for trying features.",
          "Results of filters, textures, and frames may look different depending on the device, the photo, and the save quality setting.",
        ],
      },
      {
        title: "4. Data and User Responsibility",
        list: [
          "Photos, adjustments, presets, history, and film rolls are stored locally on your device; Daydream Factory does not access their contents.",
          "In-app data may be lost when changing devices or deleting the app. You are responsible for saving important results to your photo library or backing them up separately.",
        ],
      },
      {
        title: "5. Disclaimer",
        list: [
          "Veil is provided 'as is'. Some features may be delayed or limited depending on external services such as billing.",
          "To the extent permitted by applicable law, liability for damages arising from use of the service or data loss may be limited.",
        ],
      },
      {
        title: "6. Intellectual Property",
        body:
          "Rights to the Veil app, design, logo, and filter and scene content belong to Daydream Factory. Unauthorized copying, distribution, or reverse engineering is prohibited. Rights to the photos you edit remain yours.",
      },
      {
        title: "7. Changes to These Terms",
        body:
          "These terms may be updated to improve the service or to reflect legal changes. Significant changes will be announced in the app or on this page.",
      },
      {
        title: "8. Contact",
        body: "Questions about these terms: support@daydreamfactory.net",
      },
    ],
  },
};

const VeilTerms: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default VeilTerms;
